import type { ExpandedState } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';

import TablePagination from './table-pagination-v7';
import {
  type DataTableProps,
  TableView,
  type TableViewProps,
} from './table-view';
import type { SortingState } from './types';
import { useDebouncedFilter } from './use-debounced-filter';
import { useResetExpandedOnDataChange } from './use-reset-expanded-on-data-change';

export interface ServerDataTablePagination {
  /**
   * Notified with the next zero-based page index.
   */
  onPageChange: (pageIndex: number) => void;
  /**
   * Notified with the next page size; omit to hide the page-size control.
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Total number of pages reported by the server.
   */
  pageCount: number;
  /**
   * The current zero-based page index.
   */
  pageIndex: number;
  /**
   * The current page size.
   */
  pageSize?: number;
  /**
   * Page-size choices offered in the toolbar.
   */
  pageSizeOptions?: number[];
}

export interface ServerDataTableProps<TData>
  extends
    DataTableProps<TData>,
    // Not shared via `DataTableProps`: a client table holds every row already,
    // so there is nothing for it to fetch.
    Pick<TableViewProps<TData>, 'infinite'> {
  /**
   * The controlled global filter value.
   */
  filter?: string;
  /**
   * Whether an active filter is applied; defaults to whether `filter` is set, so
   * pass it explicitly when the filter surface is external (e.g. a
   * `ListActionsHeader`).
   */
  filtering?: boolean;
  /**
   * Notified with the debounced filter value; omit to hide the filter input
   * (which renders in the pager toolbar, so requires `pagination`).
   */
  onFilterChange?: (value: string) => void;
  /**
   * Notified with the next sort order.
   */
  onSortChange?: (sort: SortingState) => void;
  /**
   * Server pagination state and callbacks; omit for an unpaged (e.g. infinite) list.
   */
  pagination?: ServerDataTablePagination;
  /**
   * The controlled sort order.
   */
  sort?: SortingState;
}

/**
 * A data table for server-driven data: sort, filter, and pagination are
 * controlled and reported to the server, which returns the rows to display.
 */
export const ServerDataTable = <TData,>(props: ServerDataTableProps<TData>) => {
  const {
    'aria-label': ariaLabel,
    className,
    columns,
    data,
    expandedRow,
    filter = '',
    filtering,
    infinite,
    instanceRef,
    isRowActive,
    loading = false,
    multiSort = true,
    noData,
    onFilterChange,
    onRowClick,
    onSortChange,
    pagination,
    resizable,
    rowClassName,
    sort = [],
    stickyHeader,
    virtualized,
  } = props;

  const [expanded, setExpanded] = useState<ExpandedState>({});
  useResetExpandedOnDataChange(data, setExpanded);

  const table = useReactTable({
    columns,
    data,
    enableMultiSort: multiSort,
    // Without a sort handler there is nowhere to report a sort, so keep the
    // headers non-interactive (mirrors how omitting onFilterChange hides the
    // filter input).
    enableSorting: Boolean(onSortChange),
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => Boolean(expandedRow),
    manualFiltering: true,
    manualSorting: true,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: updater =>
      onFilterChange?.(
        typeof updater === 'function' ? updater(filter) : updater,
      ),
    onSortingChange: updater => {
      onSortChange?.(typeof updater === 'function' ? updater(sort) : updater);

      // A page index into the old order is meaningless once re-sorted.
      if (pagination && pagination.pageIndex !== 0) pagination.onPageChange(0);
    },
    // Descending is the order a first click is usually reaching for, so every
    // column starts there rather than letting v8 infer a direction from the
    // first row's value. Text columns opt out with `sortDescFirst: false`.
    sortDescFirst: true,
    state: { expanded, globalFilter: filter, sorting: sort },
  });

  const { currentFilter, onFilteredChange } = useDebouncedFilter({
    globalFilter: filter,
    initialFilter: filter,
    onPageChange: pagination?.onPageChange ?? (() => {}),
    pageIndex: pagination?.pageIndex ?? 0,
    setGlobalFilter: table.setGlobalFilter,
  });

  // Opt out of the pager's default filter autofocus so mounting a table never
  // steals focus and scrolls the page to its filter.
  const toolbar = pagination ? (
    <TablePagination
      filterStr={currentFilter}
      noFilter={!onFilterChange}
      noFocus
      noSizeOptions={!pagination.onPageSizeChange}
      onFilteredChange={onFilteredChange}
      onPageChange={pagination.onPageChange}
      onPageSizeChange={pagination.onPageSizeChange ?? (() => {})}
      page={pagination.pageIndex}
      pages={pagination.pageCount}
      pageSize={pagination.pageSize ?? 10}
      rawData={data}
      sizeOptions={(pagination.pageSizeOptions ?? [5, 10, 25, 50, 100]).map(
        value => ({ key: value, text: String(value), value }),
      )}
    />
  ) : null;

  return (
    <TableView
      aria-label={ariaLabel}
      className={className}
      expandedRow={expandedRow}
      filtering={filtering ?? Boolean(filter)}
      infinite={infinite}
      instanceRef={instanceRef}
      isRowActive={isRowActive}
      loading={loading}
      noData={noData}
      onRowClick={onRowClick}
      resizable={resizable}
      rowClassName={rowClassName}
      stickyHeader={stickyHeader}
      table={table}
      toolbar={toolbar}
      virtualized={virtualized}
    />
  );
};

export default ServerDataTable;
